import { useEffect, useState } from 'react';
import { API_CONFIG } from '../lib/apiConfig';
import { fetchWithCache } from '../lib/fetchWithCache';
import {
  PerCrownNativeContextBoundary,
  PerCrownNativeWindow,
  SupplyShedDriveTime,
} from '../lib/types';

// Loads the static GeoJSON written by `scripts/generate-per-crown-data.py`'s
// second pass — a small (~1 km square) bundle of actual crown polygons
// centered on the AGB-weighted centroid of the shed. Consumed by:
//   1. PerCrownNativeOverlay → renders the polygons on the map
//   2. PerCrownContent       → reads `crownCount` for the trigger card and
//                              `centerLatLng` / `flyZoom` for the camera fly
// Both consumers call this hook independently; `fetchWithCache` dedupes to
// one network round-trip.
//
// 404-tolerant: when no window has been published for the (mill, dt) the
// hook returns `data: null` with no error — the trigger card is hidden in
// that state, the overlay never paints. There is no stub fallback (the
// 12-ish MB GeoJSON isn't worth bundling).

interface Result {
  data: PerCrownNativeWindow | null;
  isLoading: boolean;
  error: string | null;
}

// Mills where v1.2 per-crown artifacts have been published. Used by callers
// to short-circuit the fetch instead of hitting S3 with a guaranteed 404 on
// every layer-open. v1.3 will broaden this; expect this list to grow.
const PER_CROWN_COVERAGE: ReadonlySet<string> = new Set(['california-sierra-nevada']);

export const hasPerCrownCoverage = (millCode: string | null): boolean =>
  millCode !== null && PER_CROWN_COVERAGE.has(millCode);

// The single (mill, drive_time) tuple where v1.2 per-crown data lives.
// Drives the dropdown's locality pill and the "View in California Sierra
// Nevada" CTA on the empty-state panel. v1.3 will broaden this.
export const PER_CROWN_AVAILABLE_MILL_CODE = 'california-sierra-nevada';
export const PER_CROWN_AVAILABLE_DRIVE_TIME: SupplyShedDriveTime = 60;

export const isPerCrownAvailable = (
  millCode: string | null,
  driveTime: SupplyShedDriveTime,
): boolean =>
  millCode === PER_CROWN_AVAILABLE_MILL_CODE &&
  driveTime === PER_CROWN_AVAILABLE_DRIVE_TIME;

const parseWindow = (raw: unknown): PerCrownNativeWindow | null => {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const features = Array.isArray(r.features) ? (r.features as unknown[]) : null;
  const meta = (r.metadata as Record<string, unknown> | undefined) ?? null;
  if (!features || !meta) return null;

  const center = meta.centerLatLng;
  const bbox = meta.bbox;
  const binEdges = meta.binEdgesAgbKg;
  const binColors = meta.binColors;
  if (
    !Array.isArray(center) || center.length !== 2 ||
    !Array.isArray(bbox) || bbox.length !== 4 ||
    !Array.isArray(binEdges) ||
    !Array.isArray(binColors) || binColors.length !== 5
  ) {
    return null;
  }

  return {
    features,
    centerLatLng: [Number(center[0]), Number(center[1])],
    bbox: [Number(bbox[0]), Number(bbox[1]), Number(bbox[2]), Number(bbox[3])],
    flyZoom: Number(meta.flyZoom ?? 16),
    binEdgesAgbKg: (binEdges as unknown[]).map((v) => Number(v)),
    binColors: (binColors as unknown[]).map((v) => String(v)),
    crownCount: Number(meta.crownCount ?? features.length),
    contextBoundary: parseContextBoundary(meta.contextBoundary),
  };
};

const parseContextBoundary = (
  raw: unknown,
): PerCrownNativeContextBoundary | null => {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const name = typeof r.name === 'string' ? r.name : null;
  const geom = r.geometry as { type?: unknown; coordinates?: unknown } | null;
  if (!name || !geom) return null;
  if (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon') return null;
  if (!Array.isArray(geom.coordinates)) return null;
  return {
    name,
    geometry: {
      type: geom.type as 'Polygon' | 'MultiPolygon',
      coordinates: geom.coordinates,
    },
  };
};

export const usePerCrownNativeWindow = (
  millCode: string | null,
  driveTime: SupplyShedDriveTime,
  enabled: boolean = true,
): Result => {
  const [data, setData] = useState<PerCrownNativeWindow | null>(null);
  const [isLoading, setIsLoading] = useState(enabled && millCode !== null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !millCode) {
      setData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const url = `${API_CONFIG.s3.baseUrl}/${API_CONFIG.s3.paths.perCrownNative(millCode, driveTime)}`;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchWithCache(url, (r) => r.json().then(parseWindow))
      .then((parsed) => {
        if (cancelled) return;
        setData(parsed);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        // 404 / network failure → silently null. No coverage isn't an
        // error worth surfacing in the UI; the trigger card just hides.
        setData(null);
        setIsLoading(false);
        setError(null);
      });

    return () => {
      cancelled = true;
    };
  }, [millCode, driveTime, enabled]);

  return { data, isLoading, error };
};

export { parseWindow };
